import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useShop } from "@/context/ShopContext";
import { CartTimer } from "@/components/shop/CartTimer";
import { eur } from "@/lib/api";
import { useShippingConfig, freeOver } from "@/lib/shipping";
import { ShippingSummary } from "@/components/shop/ShippingSummary";
import { ProductImage } from "@/components/shop/ProductImage";

export const CartSheet = () => {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal } = useShop();
  const navigate = useNavigate();
  const FREE_SHIPPING = freeOver(useShippingConfig(), "italy");
  const missing = FREE_SHIPPING ? Math.max(0, FREE_SHIPPING - cartTotal) : 0;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="flex w-full flex-col border-black/10 bg-[#FFF8EC] sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-stone-900">Il tuo carrello</SheetTitle>
        </SheetHeader>
        <CartTimer testid="cart-drawer-timer" className="mt-2" />
        {cart.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-stone-600" data-testid="cart-empty">
            <ShoppingBag className="h-10 w-10" />
            <p>Il carrello è vuoto</p>
          </div>
        ) : (
          <>
            <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2 py-4">
              {cart.map((i) => (
                <div key={i.product_id} className="flex gap-3 rounded-xl border border-black/[0.08] bg-[#FFFFFF] p-3" data-testid={`cart-item-${i.product_id}`}>
                  <ProductImage src={i.image} alt={i.name} w={300} width={64} height={80} className="h-20 w-16 rounded-lg object-cover" />
                  <div className="flex flex-1 flex-col">
                    <p className="line-clamp-2 text-sm font-semibold text-stone-900">{i.name}</p>
                    <p className="font-mono text-sm text-amber-600">{eur(i.price)}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center rounded-full border border-black/10">
                        <button className="p-1.5 hover:text-amber-600" onClick={() => updateQty(i.product_id, i.quantity - 1)} data-testid={`cart-dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                        <span className="w-6 text-center font-mono text-sm" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</span>
                        <button className="p-1.5 hover:text-amber-600" onClick={() => updateQty(i.product_id, i.quantity + 1)} data-testid={`cart-inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <button className="text-stone-500 hover:text-rose-500" onClick={() => removeFromCart(i.product_id)} data-testid={`cart-remove-${i.product_id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-3 border-t border-black/10 pt-4">
              {FREE_SHIPPING > 0 && <p className="text-xs text-stone-600" data-testid="cart-shipping-hint">
                {missing > 0 ? `Aggiungi ${eur(missing)} per la spedizione gratuita in Italia` : "Spedizione gratuita in Italia sbloccata!"}
              </p>}
              <div onClick={() => setCartOpen(false)}><ShippingSummary testid="cart-shipping-summary" /></div>
              <div className="flex justify-between font-display text-lg">
                <span>Subtotale</span>
                <span className="font-mono font-bold text-amber-600" data-testid="cart-subtotal">{eur(cartTotal)}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                className="w-full rounded-full bg-amber-500 py-3.5 font-display font-bold text-black transition-colors hover:bg-amber-400"
              >
                Vai alla cassa
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
